import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FullCV,
  Input,
} from '@cibli/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import React from 'react';
import { ArrayPath, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useFormArray } from '@/features/common/hooks/useFormArray';

import { useValidateOnMount } from '../../hooks/useValidateOnMount';
import { useVocalFormUpdate } from '../../hooks/useVocalFormUpdate';
import { createLanguagesSchema, LanguagesFormValues } from '../../schemas/languages.schema';
import FormLayout from './FormLayout';
import NumberedItem from './NumberedItem';

interface LanguagesFormProps {
  onSubmit: (data: FullCV) => void;
  initialData?: FullCV;
  onCancel: () => void;
}

const LanguagesForm: React.FC<LanguagesFormProps> = ({ onSubmit, initialData, onCancel }) => {
  const { t } = useTranslation();

  const languagesSchema = createLanguagesSchema(t);

  const form = useForm<LanguagesFormValues>({
    resolver: zodResolver(languagesSchema),
    defaultValues: initialData || {
      languages: [{ language: '', level: '' }],
    },
  });

  const { fields, items, canAdd, handleAdd, handleRemove } = useFormArray<LanguagesFormValues>({
    form,
    name: 'languages' as ArrayPath<LanguagesFormValues>,
    maxItems: 10,
  });

  const handleSubmit = (data: LanguagesFormValues) => {
    onSubmit(data as FullCV);
  };

  useValidateOnMount(form);
  useVocalFormUpdate(form, 'languages');

  return (
    <FormLayout
      title={t('Forms.Languages.title')}
      onCancel={onCancel}
      onSubmit={form.handleSubmit(handleSubmit)}
      showAddButton={canAdd}
      onAdd={() => handleAdd({ language: '', level: '' })}
      addButtonText={t('Forms.Languages.addLanguage')}
    >
      <Form {...form}>
        <form className="space-y-6 pb-20">
          {fields.map((field, index) => (
            <NumberedItem
              key={field.id}
              index={index}
              showDelete={Array.isArray(items) && items.length > 1}
              className="items-center"
              onDelete={() => handleRemove(index)}
            >
              <div className="flex gap-4 pb-5">
                <FormField
                  control={form.control}
                  name={`languages.${index}.language`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>{t('Forms.Languages.language')}</FormLabel>
                      <FormControl>
                        <Input
                          className="h-12"
                          placeholder={t('Forms.Languages.languagePlaceholder')}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`languages.${index}.level`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>{t('Forms.Languages.level')}</FormLabel>
                      <FormControl>
                        <Input
                          className="h-12"
                          placeholder={t('Forms.Languages.levelPlaceholder')}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </NumberedItem>
          ))}
        </form>
      </Form>
    </FormLayout>
  );
};

export default LanguagesForm;
